import { useState } from "react";
import axios from "axios";
import { WAITLIST_SHEETS_URL } from "@/lib/rooted";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email-only waitlist form. Renders inline (email input + submit button on one row).
 * Variant `size` controls the pill/button proportions.
 */
export const WaitlistForm = ({ variant = "default", testId = "waitlist-form" }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const emailIsValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter a valid email.");
      return;
    }
    if (!emailIsValid(trimmed)) {
      setError("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      // Google Sheets mirror (fire-and-forget, no-cors).
      await fetch(WAITLIST_SHEETS_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({ name: "", email: trimmed }),
        headers: { "Content-Type": "application/json" },
      });
      // Persist to our own MongoDB-backed waitlist endpoint.
      await axios.post(`${API}/waitlist`, { first_name: "", email: trimmed });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something didn't take. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={`${testId}-success`}
        className="rise max-w-xl mx-auto text-center"
      >
        <p className="font-display text-2xl md:text-3xl text-rooted">Thank you.</p>
        <p className="mt-3 font-body text-base text-muted-rooted">
          We&apos;ll write when there&apos;s something worth saying.
        </p>
      </div>
    );
  }

  const btnClass =
    variant === "large"
      ? "btn-pill text-base md:text-lg px-7 md:px-8 py-3.5 md:py-4"
      : "btn-pill text-sm md:text-base px-6 md:px-7 py-3 md:py-3.5";

  return (
    <form
      data-testid={testId}
      onSubmit={onSubmit}
      className="max-w-lg mx-auto w-full"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-stretch">
        <input
          data-testid={`${testId}-email`}
          className="field flex-1 !border-b sm:!border-b sm:pr-4"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
        />
        <button
          type="submit"
          data-testid={`${testId}-submit`}
          disabled={submitting}
          className={`${btnClass} whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {submitting ? "Planting…" : "Join Waitlist"}
        </button>
      </div>

      {error && (
        <p
          data-testid={`${testId}-error`}
          className="mt-3 text-sm text-[#c97c5a] font-body text-center sm:text-left"
        >
          {error}
        </p>
      )}
    </form>
  );
};

export default WaitlistForm;
